/**
 * ThemeToggle — Button to cycle between light / dark / system themes.
 * Can render as a compact icon button or a full dropdown selector.
 */
import { Sun, Moon, Monitor } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import { AnimatedIcon, iconToneClass, useAnimatedIconState } from '@/components/internal/AnimatedIcon';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const options = [
  { value: 'light', label: 'Claro', icon: Sun },
  { value: 'dark', label: 'Escuro', icon: Moon },
  { value: 'system', label: 'Sistema', icon: Monitor },
];

export function ThemeToggle({ className = '' }) {
  const { theme, resolved, setTheme } = useTheme();

  const ActiveIcon = resolved === 'dark' ? Moon : Sun;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className={`inline-flex items-center justify-center rounded-md p-2 text-foreground-muted hover:bg-surface-hover hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${className}`}
          aria-label="Alterar tema"
        >
          <ActiveIcon className="h-4 w-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-36">
        {options.map(({ value, label, icon: Icon }) => (
          <DropdownMenuItem
            key={value}
            onClick={() => setTheme(value)}
            className={theme === value ? 'bg-accent' : ''}
          >
            <Icon className="mr-2 h-4 w-4" />
            {label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Simple icon toggle (no dropdown) — just toggles dark/light with rotation animation.
 */
export function ThemeToggleSimple({ className = '' }) {
  const { resolved, toggleTheme } = useTheme();
  const { hovered, playSignal, handlers } = useAnimatedIconState(false);
  const dark = resolved === 'dark';

  return (
    <button
      onClick={toggleTheme}
      {...handlers}
      className={`inline-flex items-center justify-center rounded-md p-2 text-foreground-muted hover:bg-surface-hover hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${className}`}
      aria-label={dark ? 'Mudar para tema claro' : 'Mudar para tema escuro'}
    >
      {/* O botão mostra para onde o tema vai: sol no escuro, lua no claro. Ao
          trocar, o ícone novo entra e toca uma vez (playOnMount). */}
      <AnimatedIcon
        key={dark ? 'sun' : 'moon'}
        name={dark ? 'theme-sun' : 'theme-moon'}
        fallback={dark ? Sun : Moon}
        playSignal={playSignal}
        playOnMount
        colorKey={String(hovered)}
        className={`h-5 w-5 ${iconToneClass({ hovered })}`}
      />
    </button>
  );
}
